import React from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { getUserNameById } from '../../UserContext.js';
import CopyableIdCell from '../../../common/CopyableIdCell.js';

const statusRenderer = (params) => {
  const status = params.value;
  return (
    <span
      style={{
        color:
          status === 'Completed' || status === 'All Done' || status === 'Settled'
            ? '#2e7d32'
            : status === 'Incomplete'
            ? '#ed6c02'
            : status === 'Partial'
            ? '#ff9800'
            : '#666',
        fontWeight: 'bold',
      }}
    >
      {status || 'Incomplete'}
    </span>
  );
};

const countyColumns = (handleCountyClick) => [
  {
    field: 'county',
    headerName: 'County',
    width: 150,
    sortable: true,
    filterable: true,
    renderCell: (params) => {
      const isTotalsRow = params.id === 'totals';
      if (isTotalsRow) {
        return (
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {params.value}
          </Typography>
        );
      }
      return (
        <Button
          variant="text"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleCountyClick(params.row.countyCode, params.value);
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            padding: 0,
            minWidth: 0,
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
          }}
        >
          {params.value}
        </Button>
      );
    },
  },
  { field: 'totalTasks', headerName: 'Total', width: 100, type: 'number' },
  { field: 'dataCleaningCount', headerName: 'Data Review', width: 120, type: 'number' },
  { field: 'caseReviewCount', headerName: 'Case Review', width: 120, type: 'number' },
  {
    field: 'settledCount',
    headerName: 'Settled',
    width: 100,
    type: 'number',
    renderCell: (params) => (
      <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>{params.value || 0}</span>
    ),
  },
  { field: 'status', headerName: 'Status', width: 100, renderCell: statusRenderer },
];

const municipalityColumns = (handleMuniClick) => [
  {
    field: 'municipality',
    headerName: 'Municipality',
    width: 200,
    sortable: true,
    filterable: true,
    renderCell: (params) => {
      const isTotals = params.id === 'totals';
      if (isTotals) {
        return (
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {params.value}
          </Typography>
        );
      }
      return (
        <Button
          variant="text"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            const match = /\(([^)]+)\)$/.exec(params.value || '');
            const code = match ? match[1] : null;
            handleMuniClick(code, params.value);
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            padding: 0,
            minWidth: 0,
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
          }}
        >
          {params.value}
        </Button>
      );
    },
  },
  { field: 'totalTasks', headerName: 'Total', width: 110, type: 'number' },
  { field: 'dataCleaningCount', headerName: 'Data Review', width: 130, type: 'number' },
  { field: 'caseReviewCount', headerName: 'Case Review', width: 130, type: 'number' },
  { field: 'settledCount', headerName: 'Settled', width: 110, type: 'number' },
  { field: 'status', headerName: 'Status', width: 120, renderCell: statusRenderer },
];

const userColumns = (handleUserClick) => [
  {
    field: 'userName',
    headerName: 'User',
    width: 200,
    sortable: true,
    filterable: true,
    renderCell: (params) => {
      const isTotalsRow = params.id === 'totals';
      if (isTotalsRow) {
        return (
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {params.value || 'Total'}
          </Typography>
        );
      }
      const displayName = params.row.userId === 'Unassigned' 
        ? 'Unassigned' 
        : getUserNameById(params.row.userId) || params.value || 'N/A';
      return (
        <Button
          variant="text"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleUserClick(params.row.userId, displayName);
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            padding: 0,
            minWidth: 0,
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
          }}
        >
          {displayName}
        </Button>
      );
    },
  },
  { field: 'totalTasks', headerName: 'Total', width: 100, type: 'number' },
  { field: 'dataCleaningCount', headerName: 'Data Review', width: 120, type: 'number' },
  { field: 'caseReviewCount', headerName: 'Case Review', width: 120, type: 'number' },
  {
    field: 'settledCount',
    headerName: 'Settled',
    width: 100,
    type: 'number',
    renderCell: (params) => (
      <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>{params.value || 0}</span>
    ),
  },
  { field: 'status', headerName: 'Status', width: 100, renderCell: statusRenderer },
];

const taskColumns = [
  { 
    field: 'ParcelID', 
    headerName: 'Parcel ID', 
    width: 140,
    renderCell: (params) => (
      <CopyableIdCell 
        value={params.value} 
        label="Parcel ID"
      />
    ),
  },
  { field: 'Town', headerName: 'Town', width: 140 },
  { field: 'ReviewType', headerName: 'Review Type', width: 140 },
  {
    field: 'Assignee',
    headerName: 'Assignee',
    width: 140,
    renderCell: (params) => getUserNameById(params.value),
  },
  {
    field: 'Assigner',
    headerName: 'Assigner',
    width: 140,
    renderCell: (params) => {
      if (!params.value) {
        return <span style={{ color: '#999' }}>N/A</span>;
      }
      return getUserNameById(params.value);
    },
  },
  { field: 'DateAssigned', headerName: 'Assigned', width: 140 },
  { field: 'DateDue', headerName: 'Due', width: 140 },
  { field: 'DateCompleted', headerName: 'Completed', width: 140 },
  { field: 'status', headerName: 'Status', width: 120, renderCell: statusRenderer },
];

export const getColumns = (viewMode, { handleCountyClick, handleMuniClick, handleUserClick, groupingType }) => {
  if (viewMode === 'tasks') return taskColumns;
  if (viewMode === 'municipality') return municipalityColumns(handleMuniClick);
  if (viewMode === 'county') {
    // Return appropriate columns based on grouping type
    if (groupingType === 'Municipality') {
      return municipalityColumns(handleMuniClick);
    } else if (groupingType === 'User') {
      return userColumns(handleUserClick);
    }
    return countyColumns(handleCountyClick);
  }
  return countyColumns(handleCountyClick);
};

